import { supabase } from './supabase';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

export interface SuggestedStop {
  location_id: string;
  location_name: string;
  suggested_start_time: string;
  suggested_duration_min: number;
  transit_min_to_next?: number;
  transit_mode_to_next?: string;
  notes?: string;
  is_bonus?: boolean;
  bonus_description?: string;
  bonus_website?: string;
  bonus_category?: string;
  bonus_why_visit?: string;
  bonus_lat?: number;
  bonus_lng?: number;
}

export interface SuggestedDay {
  day_number: number;
  date: string;
  theme: string;
  reasoning?: string;
  estimated_cost_yen?: number;
  stops: SuggestedStop[];
}

export interface TripSuggestion {
  title: string;
  summary: string;
  scores: { feasibility: number; pacing: number; optimization: number };
  seasonal_note?: string;
  days: SuggestedDay[];
  excluded_locations?: { location_id: string; location_name: string; reason: string }[];
  tips?: string[];
}

export class QuotaReachedError extends Error {
  plan_slug: string;
  limit: number;
  used: number;
  period: 'lifetime' | 'month';
  upgrade_url: string;
  constructor(payload: {
    plan_slug: string;
    limit: number;
    used: number;
    period: 'lifetime' | 'month';
    upgrade_url: string;
    message?: string;
  }) {
    super(payload.message || 'AI suggestion quota reached.');
    this.name = 'QuotaReachedError';
    this.plan_slug = payload.plan_slug;
    this.limit = payload.limit;
    this.used = payload.used;
    this.period = payload.period;
    this.upgrade_url = payload.upgrade_url;
  }
}

export async function generateTripSuggestion(params: {
  locations: any[];
  visitedLocations: any[];
  prompt: string;
  startDate: string;
  endDate: string;
  flexibility: number | null;
}): Promise<TripSuggestion> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) throw new Error('Not authenticated');

  const response = await fetch(`${SUPABASE_URL}/functions/v1/suggest-trip`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${session.access_token}`,
      'apikey': SUPABASE_ANON_KEY,
    },
    body: JSON.stringify(params),
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: 'Unknown error' }));
    if (response.status === 429 && err.code === 'quota_reached') {
      throw new QuotaReachedError({
        plan_slug: err.plan_slug ?? 'free',
        limit: err.limit ?? 1,
        used: err.used ?? 0,
        period: err.period ?? 'lifetime',
        upgrade_url: err.upgrade_url ?? '/credits?tab=plans',
        message: err.error,
      });
    }
    throw new Error(err.error || `API error ${response.status}`);
  }

  return response.json();
}

export async function saveSuggestion(params: {
  userId: string;
  prompt: string;
  startDate: string;
  endDate: string;
  flexibility: number | null;
  suggestion: TripSuggestion;
}): Promise<string | null> {
  await supabase
    .from('trip_suggestions')
    .update({ status: 'dismissed', updated_at: new Date().toISOString() })
    .eq('user_id', params.userId)
    .eq('status', 'pending');

  const { data, error } = await supabase
    .from('trip_suggestions')
    .insert({
      user_id: params.userId,
      prompt: params.prompt,
      start_date: params.startDate,
      end_date: params.endDate,
      flexibility: params.flexibility,
      suggestion: params.suggestion,
      status: 'pending',
    })
    .select('id')
    .single();

  if (error) {
    console.error('Failed to save suggestion:', error);
    return null;
  }
  return data?.id || null;
}

export async function loadPendingSuggestion(userId: string): Promise<{
  id: string;
  prompt: string;
  startDate: string;
  endDate: string;
  flexibility: number | null;
  suggestion: TripSuggestion;
  createdAt: string;
} | null> {
  const { data, error } = await supabase
    .from('trip_suggestions')
    .select('*')
    .eq('user_id', userId)
    .eq('status', 'pending')
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error || !data) return null;

  return {
    id: data.id,
    prompt: data.prompt,
    startDate: data.start_date,
    endDate: data.end_date,
    flexibility: data.flexibility,
    suggestion: data.suggestion as TripSuggestion,
    createdAt: data.created_at,
  };
}

export async function markSuggestionAccepted(id: string, tripId?: string): Promise<void> {
  const update: Record<string, unknown> = {
    status: 'accepted',
    updated_at: new Date().toISOString(),
  };
  if (tripId) update.trip_id = tripId;
  await supabase
    .from('trip_suggestions')
    .update(update)
    .eq('id', id);
}

export async function markSuggestionDismissed(id: string): Promise<void> {
  await supabase
    .from('trip_suggestions')
    .update({ status: 'dismissed', updated_at: new Date().toISOString() })
    .eq('id', id);
}
